(() => {
  if (!window.WJInitCardQuickAddReveal) {
    window.WJInitCardQuickAddReveal = (root = document) => {
      const cards = root.querySelectorAll('.product-card-wrapper');
      if (!cards.length) return;

      const setRevealHeight = (card) => {
        const quickAdd = card.querySelector('.quick-add');
        if (!quickAdd) return;
        const sourceHeight = quickAdd.scrollHeight || 0;
        const height = Math.max(0, sourceHeight - 0.5);
        card.style.setProperty('--qa-reveal-height', `${height}px`);
      };

      const open = (card) => {
        setRevealHeight(card);
        card.classList.add('qa-open');
      };

      const close = (card) => {
        card.classList.remove('qa-open');
      };

      cards.forEach((card) => {
        setRevealHeight(card);
        if (card.dataset.qaRevealInit === 'true') return;
        card.dataset.qaRevealInit = 'true';

        card.addEventListener('mouseenter', () => open(card));
        card.addEventListener('mouseleave', () => close(card));

        card.addEventListener('focusin', () => open(card));
        card.addEventListener('focusout', (event) => {
          const next = event.relatedTarget || document.activeElement;
          if (!card.contains(next)) close(card);
        });

        let touchOpen = false;
        const onDocTouch = (event) => {
          if (!card.contains(event.target)) {
            touchOpen = false;
            close(card);
            document.removeEventListener('touchstart', onDocTouch);
          }
        };

        card.addEventListener(
          'touchstart',
          () => {
            if (!touchOpen) {
              touchOpen = true;
              open(card);
              setTimeout(() => document.addEventListener('touchstart', onDocTouch), 0);
            }
          },
          { passive: true }
        );
      });
    };

    window.addEventListener('resize', () => window.WJInitCardQuickAddReveal(document));
  }

  if (!window.WJInitCardQuickAddForms) {
    window.WJInitCardQuickAddForms = (root = document) => {
      const formatMoney = (cents) => {
        const normalized = typeof cents === 'string' ? Number(cents.replace('.', '')) : Number(cents);
        if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(normalized);

        const amount = normalized / 100;
        const locale = document.documentElement.lang || 'en-US';
        const currency = window.Shopify?.currency?.active || 'USD';
        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
          }).format(amount);
        } catch (error) {
          return amount.toFixed(2);
        }
      };

      const updatePrice = (form) => {
        const qty = form.querySelector('input[name="quantity"]');
        const priceDisplay = form.querySelector('[data-price-display]');
        if (!qty || !priceDisplay) return;

        let quantity = parseInt(qty.value, 10);
        if (isNaN(quantity) || quantity < 1) {
          quantity = 1;
          qty.value = 1;
        }

        const unitPrice = parseFloat(priceDisplay.getAttribute('data-variant-price'));
        const total = unitPrice * quantity;
        const [label] = priceDisplay.textContent.split('-');
        priceDisplay.textContent = `${label.trim()} - ${formatMoney(total)}`;
      };

      const clampQuantity = (input) => {
        if (!input) return;
        const min = Math.max(1, parseInt(input.min || input.dataset.min || '1', 10) || 1);
        const value = parseInt(input.value, 10);
        if (isNaN(value) || value < min) {
          input.value = min;
        }
      };

      const getVariants = (form) => {
        const element = form.querySelector('.ProductVariantsJSON');
        if (!element) return [];
        try {
          return JSON.parse(element.textContent);
        } catch (error) {
          return [];
        }
      };

      const getSelectedOptions = (form) => {
        const options = {};
        form.querySelectorAll('.variant-group').forEach((group) => {
          const position = parseInt(group.dataset.optionPosition, 10);
          const checked = group.querySelector('input.variant-option-input:checked');
          if (checked) options[position] = checked.value;
        });
        return options;
      };

      const findVariantByOptions = (variants, options) =>
        variants.find(
          (variant) =>
            (!options[1] || variant.option1 === options[1]) &&
            (!options[2] || variant.option2 === options[2]) &&
            (!options[3] || variant.option3 === options[3])
        );

      const setState = (form, variant) => {
        if (!variant) return;
        const idInput = form.querySelector('.product-variant-id');
        const button = form.querySelector('.quick-add__submit');
        const priceEl = form.querySelector('[data-price-display]');
        if (idInput) idInput.value = variant.id;
        if (button) button.toggleAttribute('disabled', !variant.available);

        if (priceEl) {
          priceEl.setAttribute('data-variant-price', variant.price);
          updatePrice(form);
        }

        const qtyWrap = form.querySelector('.quantity');
        if (qtyWrap) {
          qtyWrap.classList.toggle('unavailable', !variant.available);
          qtyWrap.querySelectorAll('input, button').forEach((el) => {
            el.disabled = !variant.available;
          });
          const qtyInput = qtyWrap.querySelector('input[name="quantity"]');
          if (qtyInput) {
            qtyInput.min = 1;
            if (!variant.available) qtyInput.value = 1;
          }
        }
      };

      const refreshSelectedClasses = (form) => {
        form.querySelectorAll('.variant-group').forEach((group) => {
          group.querySelectorAll('.variant-option-input').forEach((input) => {
            const label = form.querySelector(`label[for="${input.id}"]`);
            if (!label) return;
            label.classList.toggle('is-selected', input.checked);
          });
        });
      };

      const getVariantMediaMap = (form) => {
        const element = form.querySelector('.VariantMediaJSON');
        if (!element) return {};
        try {
          const data = JSON.parse(element.textContent);
          return data.reduce((map, item) => {
            map[item.id] = item;
            return map;
          }, {});
        } catch (error) {
          return {};
        }
      };

      const updateCardMedia = (form, variant) => {
        if (!variant) return;
        const mediaMap = getVariantMediaMap(form);
        const media = mediaMap[variant.id];
        if (!media || !media.has_media) return;

        const card = form.closest('.card-wrapper');
        if (!card) return;
        const primaryImg = card.querySelector('img[data-card-primary-image]');
        if (!primaryImg) return;

        const parts = [];
        if (media.src_165) parts.push(`${media.src_165} 165w`);
        if (media.src_360) parts.push(`${media.src_360} 360w`);
        if (media.src_533) parts.push(`${media.src_533} 533w`);
        if (media.src_720) parts.push(`${media.src_720} 720w`);
        if (media.src_940) parts.push(`${media.src_940} 940w`);
        if (media.src_1200) parts.push(`${media.src_1200} 1200w`);
        if (media.src_1500) parts.push(`${media.src_1500} 1500w`);
        if (media.src_1066) parts.push(`${media.src_1066} 1066w`);

        primaryImg.setAttribute('srcset', parts.join(', '));
        primaryImg.setAttribute('src', media.src_720 || media.src_533 || media.src_360 || media.src_165);
        if (media.alt) primaryImg.setAttribute('alt', media.alt);
        if (media.width) primaryImg.setAttribute('width', media.width);
        if (media.height) primaryImg.setAttribute('height', media.height);
      };

      const valuePossible = (variants, position, value, selected) =>
        variants.some(
          (variant) =>
            variant.available &&
            variant[`option${position}`] === value &&
            (!selected[1] || position === 1 || variant.option1 === selected[1]) &&
            (!selected[2] || position === 2 || variant.option2 === selected[2]) &&
            (!selected[3] || position === 3 || variant.option3 === selected[3])
        );

      const syncAvailability = (form) => {
        const variants = getVariants(form);
        const selected = getSelectedOptions(form);

        form.querySelectorAll('.variant-group').forEach((group) => {
          const position = parseInt(group.dataset.optionPosition, 10);

          group.querySelectorAll('input.variant-option-input').forEach((input) => {
            const label = form.querySelector(`label[for="${input.id}"]`);
            const possible = valuePossible(variants, position, input.value, selected);
            input.disabled = !possible;
            if (label) {
              label.classList.toggle('is-unavailable', !possible);
              label.setAttribute('aria-disabled', !possible ? 'true' : 'false');
            }
          });

          const checked = group.querySelector('input.variant-option-input:checked');
          if (!checked || checked.disabled) {
            const fallback = group.querySelector('input.variant-option-input:not([disabled])');
            if (fallback) fallback.checked = true;
          }
        });
      };

      root.querySelectorAll('form[data-type="add-to-cart-form"]').forEach((form) => {
        if (form.dataset.qaFormInit === 'true') return;
        form.dataset.qaFormInit = 'true';

        const variants = getVariants(form);
        if (!variants.length) return;

        const qty = form.querySelector('input[name="quantity"]');
        if (qty) {
          qty.min = 1;
          clampQuantity(qty);
          updatePrice(form);
          ['change', 'input'].forEach((eventName) =>
            qty.addEventListener(eventName, () => {
              clampQuantity(qty);
              updatePrice(form);
            })
          );
          const wrapper = qty.closest('.quantity');
          if (wrapper) {
            wrapper
              .querySelectorAll('button[name="minus"], button[name="plus"]')
              .forEach((button) =>
                button.addEventListener('click', () =>
                  setTimeout(() => {
                    clampQuantity(qty);
                    updatePrice(form);
                  }, 10)
                )
              );
          }
        }

        form.querySelectorAll('input.variant-option-input').forEach((input) => {
          input.disabled = false;
          const label = form.querySelector(`label[for="${input.id}"]`);
          if (label) label.classList.remove('is-unavailable');
        });

        form.querySelectorAll('.variant-group').forEach((group) => {
          let checked = group.querySelector('input.variant-option-input:checked');
          if (!checked) {
            const first = group.querySelector('input.variant-option-input');
            if (first) first.checked = true;
          }
        });

        syncAvailability(form);

        let current =
          findVariantByOptions(variants, getSelectedOptions(form)) ||
          variants.find((variant) => variant.available) ||
          variants[0];

        setState(form, current);
        updateCardMedia(form, current);
        refreshSelectedClasses(form);

        form.querySelectorAll('input.variant-option-input').forEach((input) => {
          input.addEventListener('change', () => {
            syncAvailability(form);
            refreshSelectedClasses(form);

            const selected = getSelectedOptions(form);
            let variant = findVariantByOptions(variants, selected);

            if (!variant) {
              variant =
                variants.find(
                  (candidate) =>
                    candidate.available &&
                    (!selected[1] || candidate.option1 === selected[1]) &&
                    (!selected[2] || candidate.option2 === selected[2]) &&
                    (!selected[3] || candidate.option3 === selected[3])
                ) ||
                variants.find((candidate) => candidate.available) ||
                variants[0];

              if (variant) {
                [1, 2, 3].forEach((position) => {
                  const value = variant[`option${position}`];
                  const radio = form.querySelector(
                    `.variant-group[data-option-position="${position}"] input.variant-option-input[value="${CSS.escape(value)}"]`
                  );
                  if (radio && !radio.disabled) radio.checked = true;
                });
                refreshSelectedClasses(form);
              }
            }

            setState(form, variant);
            updateCardMedia(form, variant);
          });
        });
      });
    };
  }

  const run = () => {
    window.WJInitCardQuickAddReveal(document);
    window.WJInitCardQuickAddForms(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
